import { createAction, props } from '@ngrx/store';
import { Session } from '@ory/client';
import { MemberDto, RuleDto } from '@careapp-group/ng-sdk';

const APPLICATION_AUTHORIZED = '[Application] Auth OK';
const APPLICATION_READY = '[Application] Ready';
const APPLICATION_SESSION_READY = '[Application] Session Ready';
const APPLICATION_LOGIN_PROCESS_COMPLETE = '[Application] Login Complete';

export enum SessionCheckReason {
  appStartup,
  afterLogin,
  afterRegistration,
  afterRecovery,
}

/**
 * Trigger a check to make sure the user's session is valid and ready
 * This is performed during initial startup and after the user has
 * completed a login flow.
 *
 * Checks that the session is working and that we have the user's identity.
 * Redirects as directed by Kratos, eg to start an AAL2 session or to
 * perform a session refresh.
 */
export const PerformSessionCheck = createAction(
  '[Application] perform session check',
  props<{ reason: SessionCheckReason }>()
);

/**
 * Indicates that the user's session is ready to be used and we can begin
 * the authenticated part of startup.
 *
 * This action is dispatched after a SessionCheck on initial startup and
 * after login is complete.
 */
export const SessionReadyAction = createAction(
  APPLICATION_SESSION_READY,
  props<{ session: Session; userId: string; reason: SessionCheckReason }>()
);

/**
 * Indicates that the app is ready for use. This action is fired once
 * on startup to indicate the app is ready but the user does not have
 * a session.
 */
export const ApplicationReady = createAction(APPLICATION_READY);

/**
 * Triggers the initial startup fetched to be redone. This is usually
 * done to refresh a user's membership and permissions after accepting
 * an invite.
 */
export const RefreshAppStartup = createAction('[Application] Refresh app startup');

/**
 * Initial required requests have all fired and the user is logged in.
 * This action is dispatched during initial load if the user is authorised,
 * and after the login flow and session check has completed
 */
export const ApplicationAuthorized = createAction(
  APPLICATION_AUTHORIZED,
  props<{ user: MemberDto; organisationId: string; roles?: string[] }>()
);

/**
 * Dispatched when user interaction causes the app to become authenticated
 */
export const ApplicationLoginComplete = createAction(APPLICATION_LOGIN_PROCESS_COMPLETE);

export const ResponseSuccessFetchAbility = createAction(
  '[Application][Success] fetch ability',
  props<{ ability: RuleDto[] }>()
);
